import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import { SxProps } from "@mui/material/styles";
import * as React from "react";

import useDesktopLayout from "../../hooks/useDesktopLayout";
import { DesktopDrawerCollapseButton } from "./DesktopDrawerHeadings";

export const DRAWER_WIDTH = 240;
export const DRAWER_WIDTH_COLLAPSED = 56;

type Props = {
    sx?: SxProps;
};

/** Permanent desktop sidebar (sm+); width driven by `isSidebarCollapsed`. */
export function DesktopDrawer({ children, sx }: React.PropsWithChildren<Props>) {
    const { isSidebarCollapsed } = useDesktopLayout();

    return (
        <Drawer
            variant="permanent"
            component="nav"
            open
            sx={[
                theme => ({
                    // Below the content frame so its shadow reads on the seam.
                    zIndex: 0,
                    "& .MuiDrawer-paper": {
                        position: "relative",
                        height: "100%",
                        width: isSidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
                        backgroundColor: theme.palette.background.default,
                        borderRight: "none",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        transition: theme.transitions.create("width", {
                            duration: theme.transitions.duration.standard,
                        }),
                    },
                    "& .MuiListItemText-root, & .MuiListSubheader-root": {
                        opacity: isSidebarCollapsed ? 0 : 1,
                        cursor: isSidebarCollapsed ? "default" : "auto",
                        transition: theme.transitions.create("opacity", {
                            duration: theme.transitions.duration.shorter,
                        }),
                    },
                    // Prevent text wrapping for a smooth collapse transition
                    "& .MuiListItem-root, & .MuiListSubheader-root": {
                        minWidth: DRAWER_WIDTH,
                    },
                }),
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                }}
            >
                {children}
            </Box>
            <DesktopDrawerCollapseButton />
        </Drawer>
    );
}
